import { ARENA, VIRTUAL_WIDTH, VIRTUAL_HEIGHT } from '../../config/constants.js';

const TRAUMA_DECAY = 2.6; // per second — faster falloff so shake doesn't linger/stack when hits land close together
const MAX_SHAKE_OFFSET = 4; // virtual px, at trauma == 1
const FOLLOW_LERP_SPEED = 6; // per second — how quickly the camera closes in on the player
const ZOOM_LERP_SPEED = 4; // per second — a little slower than position follow so zoom changes read as a deliberate pull-back, not a snap
// Below this the fight reads as too small/far away to actually play by —
// worst case (player and boss at opposite arena corners) needs ~0.38, so
// this only ever bites on top of that (e.g. a secondary boss/herald pair
// widening the spread further), and even then keeps things playable.
const MIN_ZOOM = 0.35;
// Padding so a far-apart player/boss don't sit flush against the frame
// edge — telegraphs/bullets need a little room to read before they'd
// otherwise clip off-screen the instant separation ticks up further.
const ZOOM_FIT_MARGIN = 1.15;

/**
 * The arena is larger than one screen, so the camera follows a target
 * (smoothed, not a hard lock) instead of showing the whole fight at once.
 * During the boss stage, Game.js._cameraFollowTarget() feeds it the
 * player/boss midpoint rather than the player alone, so boss-centered
 * telegraphs and bullets stay in frame far more often — a plain wave-stage
 * minion telegraph can still end up off-screen (accepted tradeoff there;
 * see also CombatSystem's on-screen-only auto-targeting for the flip side
 * of this same arena-bigger-than-viewport tradeoff). The view window is
 * clamped so it never shows outside the arena rectangle.
 *
 * Shake still uses the standard "trauma" model (Squirrel Eiserloh's GDC
 * talk): trauma decays linearly over time but shake magnitude scales with
 * trauma^2, so small hits barely shake while big hits (phase change, death)
 * punch hard and then fall off fast rather than lingering.
 *
 * The midpoint follow alone isn't enough once the player and boss get far
 * enough apart — the view window never grew to compensate, so one of them
 * would render outside the fixed 384x216 frame despite the midpoint being
 * perfectly centered. `zoom` (see `update`/`_desiredZoom`) pulls the view
 * back to keep both in frame instead; `worldToScreen` and `_clampToArena`
 * both fold it in, so 1 world unit == `zoom` virtual px, not always 1:1.
 */
export class Camera {
  constructor() {
    this.x = ARENA.x + ARENA.width / 2;
    this.y = ARENA.y + ARENA.height / 2;
    this.zoom = 1;
    this.trauma = 0;
    this.shakeX = 0;
    this.shakeY = 0;
  }

  addTrauma(amount) {
    this.trauma = Math.min(1, this.trauma + amount);
  }

  /**
   * @param {{x:number,y:number,spreadX?:number,spreadY?:number}} [target]
   *   typically the player, or the player/boss midpoint; camera stays put
   *   if omitted. `spreadX`/`spreadY` (both default 0) are the half-extents
   *   the view needs to cover — e.g. the max distance from `target` out to
   *   the player or boss on each axis — so the camera can zoom out to keep
   *   both in frame instead of just panning to their midpoint.
   */
  update(dt, target) {
    if (target) {
      const followT = Math.min(1, FOLLOW_LERP_SPEED * dt);
      this.x += (target.x - this.x) * followT;
      this.y += (target.y - this.y) * followT;

      const desiredZoom = this._desiredZoom(target.spreadX ?? 0, target.spreadY ?? 0);
      const zoomT = Math.min(1, ZOOM_LERP_SPEED * dt);
      this.zoom += (desiredZoom - this.zoom) * zoomT;

      this._clampToArena();
    }

    this.trauma = Math.max(0, this.trauma - TRAUMA_DECAY * dt);
    const magnitude = this.trauma * this.trauma * MAX_SHAKE_OFFSET;
    const angle = Math.random() * Math.PI * 2;
    this.shakeX = Math.cos(angle) * magnitude;
    this.shakeY = Math.sin(angle) * magnitude;
  }

  // The zoom level (<=1, never zooms in past the base 1:1 view) that fits
  // +/-spreadX/spreadY around the follow target inside the viewport, with
  // ZOOM_FIT_MARGIN of breathing room, clamped so it never zooms out past
  // MIN_ZOOM even for an extreme spread (e.g. a secondary boss on top of
  // an already-far player).
  _desiredZoom(spreadX, spreadY) {
    const neededHalfW = spreadX * ZOOM_FIT_MARGIN;
    const neededHalfH = spreadY * ZOOM_FIT_MARGIN;
    const zoomX = neededHalfW > 0 ? (VIRTUAL_WIDTH / 2) / neededHalfW : Infinity;
    const zoomY = neededHalfH > 0 ? (VIRTUAL_HEIGHT / 2) / neededHalfH : Infinity;
    return Math.min(1, Math.max(MIN_ZOOM, Math.min(zoomX, zoomY)));
  }

  // Keeps the view window inside the arena rectangle. When the arena is
  // narrower than the viewport on an axis, both clamp bounds collapse to
  // the same center point, which is what we want (no scroll on that axis).
  // The view's world-space half-extent grows as zoom shrinks (zooming out
  // shows more world), so the clamp bounds are computed from zoom rather
  // than the fixed virtual resolution.
  _clampToArena() {
    const halfW = VIRTUAL_WIDTH / 2 / this.zoom;
    const halfH = VIRTUAL_HEIGHT / 2 / this.zoom;
    const minX = ARENA.x + halfW;
    const maxX = ARENA.x + ARENA.width - halfW;
    const minY = ARENA.y + halfH;
    const maxY = ARENA.y + ARENA.height - halfH;
    this.x = minX > maxX ? ARENA.x + ARENA.width / 2 : Math.min(maxX, Math.max(minX, this.x));
    this.y = minY > maxY ? ARENA.y + ARENA.height / 2 : Math.min(maxY, Math.max(minY, this.y));
  }

  /** World-space -> virtual-backbuffer-space. */
  worldToScreen(wx, wy) {
    return {
      x: (wx - this.x) * this.zoom + VIRTUAL_WIDTH / 2 + this.shakeX,
      y: (wy - this.y) * this.zoom + VIRTUAL_HEIGHT / 2 + this.shakeY,
    };
  }
}
